import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    Snackbar,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Datepicker from '../../date-filed/DatePicker';
import { patch, post } from '../../../services/api-services';
import { StyledButton, StyledCancelButton } from "../../StyledComponent";
import { useCommon } from '../../../context/CommonContext';
import { weightRegex } from '../../../regex/regex';
import { useParams } from 'react-router-dom';

const AddPaymentDialog = ({
    open,
    onClose,
    existingProgramData,
    fetchPayments

}) => {
    const [programDate, setProgramDate] = useState(
        existingProgramData?.programDate
            ? new Date(existingProgramData.programDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0]
    );
    const [amount, setAmount] = useState('');
    const [program, setProgram] = useState(null);
    const [comments, setComments] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const isSubmittingRef = useRef(false);
    const { setSnackbar } = useCommon();
    const {id} = useParams()

    useEffect(() => {
        if (open) {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    }, [open]);

    useEffect(() => {
        if (existingProgramData?.amount) {
            
            setProgramDate(
                existingProgramData.programDate
                    ? new Date(existingProgramData?.programDate).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0]
            );
            setAmount(existingProgramData?.amount || ''); 
            setProgram(existingProgramData?.program || null);
            setComments(existingProgramData?.comments || '');
        } else {
            
            setProgramDate(new Date().toISOString().split('T')[0]);
            setAmount(existingProgramData?.remainingAmount || '');
            setProgram(null);
            setComments('');
        }
    }, [existingProgramData]);

    useEffect(() => {
        setIsSubmitDisabled(!amount || !programDate || !program);
    }, [amount, programDate, program]);

    const validatePayment = () => {
        if (parseFloat(amount) > parseFloat(existingProgramData?.remainAmount)) {
            setIsValid(true);
            setSnackbarMessage('Amount exceeds the remaining balance.');
            return false;
        }
        setIsValid(false);
        return true;
    };
    
    const handleSubmit = async () => {
        if (!validatePayment()) return;
        if (isSubmittingRef.current) return;
        
        isSubmittingRef.current = true;
        setLoading(true);
    
        const userInvoiceId = existingProgramData?.invoiceId;
        const numericAmount = parseFloat(amount);
    
        const formData = {
            amount: numericAmount,
            paidOn: programDate,
            paymentMode: program?.title || '',
            comment: comments,
            customerId: existingProgramData?.customerId,
            programId: existingProgramData?.programId,
            invoiceId: existingProgramData?.invoiceId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
    const paymentId = existingProgramData?.id;
        try {
            let res;
            if (existingProgramData?.amount) {
                res = await patch(`/Payments/${paymentId}`, formData);
            } else {
                res = await post('/Payments', formData);
            }
            if (res?.statusCode === 200 || res?.status === 200) {
                const remainingAmount = parseFloat((existingProgramData?.dueAmount - numericAmount).toFixed(2));
    
                const userProgramUpdateData = {
                    dueAmount: remainingAmount,
                    updatedAt: new Date().toISOString(),
                };
    
                await patch(`/UserPrograms/${existingProgramData?.id}`, userProgramUpdateData);
    
                if (remainingAmount <= 0) {
                    const invoiceStatus = { status: 'Paid' };
                    await patch(`/Invoices/${userInvoiceId}`, invoiceStatus);
    
                    setSnackbar({
                        openSnackbar: true,
                        message: 'Payment successful, invoice marked as Paid.',
                        severity: 'success',
                    });
                } else {
                    setSnackbar({
                        openSnackbar: true,
                        message: `Payment added, the remaining amount is ₹${remainingAmount}`,
                        severity: 'info',
                    });
                }
                fetchPayments();
                onClose();
            } else {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Payment processing failed, please try again.',
                    severity: 'error',
                });
                isSubmittingRef.current = false;
            }
        } catch (error) {
            console.error('Error submitting payment:', error);
            setSnackbar({
                openSnackbar: true,
                message: 'An error occurred while processing payment. Please try again later.',
                severity: 'error',
            });
            isSubmittingRef.current = false;
        } finally {
            setLoading(false);
        }
    };
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {existingProgramData ? 'Edit Payment Record' : 'Add Payment Record'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Amount to be Paid (in Rupees)*"
                            variant="standard"
                            fullWidth
                            value={amount || ''}
                            onChange={(e) => setAmount(e.target.value)}
                            error={(amount && (!weightRegex.test(amount) ))}
                            helperText={amount && !weightRegex.test(amount) ? "enter valid number" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} mt={-2}>
                        <Datepicker
                            label="Paid On*"
                            value={programDate}
                            onChange={(newValue) => setProgramDate(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} variant="standard" fullWidth />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            id="choose-program"
                            options={[
                                { title: 'Cash' },
                                { title: 'Cheque' },
                                { title: 'Payment Gateway' },
                                { title: 'Online Transfer (NEFT, RTGS, UPI, Paytm, etc.)' },
                            ]}
                            getOptionLabel={(option) => option.title}
                            value={program}
                            onChange={(event, newValue) => setProgram(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Payment Mode*"
                                    variant="standard"
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Comments"
                            variant="standard"
                            fullWidth
                            value={comments || ''}
                            onChange={(e) => setComments(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <StyledCancelButton onClick={onClose} color="secondary">
                    Close
                </StyledCancelButton>
                <StyledButton
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={isSubmitDisabled || loading}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </StyledButton>
            </DialogActions>
            
            <Snackbar
                open={snackbarMessage !== ''}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage('')}
                message={snackbarMessage}
            />
        </Dialog>
    );
};

export default AddPaymentDialog;
